import { useTranslator } from "@eo-locale/react";

import { string } from "yup";

import {
  Field,
  HTMLWrapper,
  Input,
  VisuallyHidden
} from "@/storybook-components";
import type { ChangeEvent } from "react";
import type { Question } from "@/types";
import { setAnswer } from "@/redux/features/questions/slice";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";

interface EmailQuestionProps {
  question: Partial<Question>;
  isAsterixRequired?: boolean;
}

export const validate = async (
  answer: string,
  question: Partial<Question>
): Promise<string[]> => {
  let schema = string();
  if (question.required) {
    schema = schema.required();
  }
  schema = schema.email();
  try {
    await schema.validate(answer);
    return [];
  } catch (err) {
    return err.errors;
  }
};

const EmailQuestion = ({
  question,
  isAsterixRequired = true
}: EmailQuestionProps): JSX.Element => {
  const { translate } = useTranslator();
  const dispatch = useAppDispatch();
  const { answers, errors, status } = useAppSelector(
    (state) => state.questions
  );

  const error = errors[question.id] || [];
  const answer = answers[question.id] || "";

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    dispatch(
      setAnswer({
        questionId: question.id,
        answer: e.target.value
      })
    );
  };

  return (
    <Field.Root
      my={4}
      id={`${question.id}`}
      invalid={error.length > 0}
      disabled={status === "saving"}
      required={question.required && isAsterixRequired}
      className={error.length > 0 ? "ehqQuestionError" : null}
    >
      <Field.Label>
        <HTMLWrapper html={question.question} />
        {question.required && (
          <VisuallyHidden>{`* ${translate("common.required")}`}</VisuallyHidden>
        )}
      </Field.Label>
      <Input
        type="email"
        value={answer}
        onChange={handleChange}
        maxLength={question.maxLength}
        size={{ base: "lg", lg: "md" }}
        data-testid="emailQuestion-input"
        name="emailQuestion-input"
        autoComplete="off"
      />
      {error.length > 0 && (
        <Field.ErrorText data-testid="emailQuestion-errorMessage">
          <Field.ErrorIcon />
          {translate(`errors.${error}`)}
        </Field.ErrorText>
      )}
      <Field.HelperText>{question.notes}</Field.HelperText>
    </Field.Root>
  );
};

export default EmailQuestion;
